import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const BASE = "/api";

export interface AiCostsCurrent {
  spent: string;
  cap: string | null;
  percentUsed: number;
  overrideActive: boolean;
  overrideUntil: string | null;
  breakdownByModel: { model: string; cost: string; calls: number }[];
  breakdownByJobType: { jobType: string; cost: string; calls: number }[];
}

export interface AiCostsHistory {
  daily: { date: string; cost: string }[];
}

export const aiCostsApi = {
  fetchCurrent: async (brandId: string): Promise<AiCostsCurrent> => {
    const r = await fetch(`${BASE}/brands/${brandId}/ai-costs/current`, { credentials: "include" });
    if (!r.ok) throw new Error("Failed to load AI costs");
    return r.json();
  },
  fetchHistory: async (brandId: string, days: number): Promise<AiCostsHistory> => {
    const r = await fetch(`${BASE}/brands/${brandId}/ai-costs/history?days=${days}`, { credentials: "include" });
    if (!r.ok) throw new Error("Failed to load history");
    return r.json() as Promise<AiCostsHistory>;
  },
  fetchJobs: async (brandId: string, limit = 50) => {
    const r = await fetch(`${BASE}/brands/${brandId}/ai-costs/jobs?limit=${limit}`, { credentials: "include" });
    if (!r.ok) throw new Error("Failed to load jobs");
    return r.json();
  },
  updateBudget: async (brandId: string, monthlyBudgetUsd: number | null) => {
    const r = await fetch(`${BASE}/brands/${brandId}/ai-costs/budget`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ monthlyBudgetUsd }),
    });
    if (!r.ok) throw new Error("Failed to update budget");
    return r.json();
  },
  setOverride: async (brandId: string, until: string) => {
    const r = await fetch(`${BASE}/brands/${brandId}/ai-costs/override`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ until }),
    });
    if (!r.ok) throw new Error("Failed to set override");
    return r.json();
  },
};

// --- TanStack Query hooks ---

export function useAiCostsCurrent(brandId: string) {
  return useQuery({
    queryKey: ["ai-costs-current", brandId],
    queryFn: () => aiCostsApi.fetchCurrent(brandId),
    enabled: !!brandId,
  });
}

export function useAiCostsHistory(brandId: string, days = 30) {
  return useQuery({
    queryKey: ["ai-costs-history", brandId, days],
    queryFn: () => aiCostsApi.fetchHistory(brandId, days),
    enabled: !!brandId,
  });
}

export function useAiCostsJobs(brandId: string, limit = 50) {
  return useQuery({
    queryKey: ["ai-costs-jobs", brandId, limit],
    queryFn: () => aiCostsApi.fetchJobs(brandId, limit),
    enabled: !!brandId,
  });
}

export function useUpdateBudget(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (monthlyBudgetUsd: number | null) => aiCostsApi.updateBudget(brandId, monthlyBudgetUsd),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-costs-current", brandId] });
      toast.success("Budget updated");
    },
    onError: () => toast.error("Failed to update budget"),
  });
}

export function useSetBudgetOverride(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (until: string) => aiCostsApi.setOverride(brandId, until),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-costs-current", brandId] });
      toast.success("Override applied");
    },
    onError: () => toast.error("Failed to apply override"),
  });
}
